import React, { MouseEventHandler, PropsWithChildren, RefObject } from 'react';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import { Background, ButtonStyle, ButtonVariant, InnerStyle } from './styles';

export type ButtonProps = {
  disabled?: boolean;
  href?: string;
  download?: boolean;
  screenReaderText?: string;
  onClick?: MouseEventHandler;
  openInNewTab?: boolean;
  background?: Background;
  type?: 'button' | 'submit' | 'reset';
  tabIndex?: number;
  backIcon?: string;
} & ComponentProps;

export type GenericButtonProps = {
  variant: ButtonVariant;
  innerRef?: RefObject<HTMLAnchorElement | HTMLButtonElement>;
} & ButtonProps;

const Button: React.FC<PropsWithChildren<GenericButtonProps>> = ({
  variant,
  background = 'light',
  disabled,
  href,
  download,
  screenReaderText,
  onClick,
  children,
  innerRef,
  type = 'button',
  tabIndex = 0,
  openInNewTab,
  ...props
}) => {
  const commonProps = {
    ...componentProps(props),
    'aria-label': screenReaderText,
    disabled,
    onClick,
    variant,
    background,
    download,
  };
  if (href) {
    return (
      <ButtonStyle
        as="a"
        href={disabled ? undefined : href}
        tabIndex={tabIndex}
        ref={innerRef as RefObject<HTMLAnchorElement>}
        data-variant={variant}
        data-background={background}
        target={openInNewTab ? '_blank' : undefined}
        {...commonProps}>
        <InnerStyle>{children}</InnerStyle>
      </ButtonStyle>
    );
  }
  return (
    <ButtonStyle
      type={type}
      tabIndex={tabIndex}
      ref={innerRef as RefObject<HTMLButtonElement>}
      data-variant={variant}
      data-background={background}
      {...commonProps}>
      <InnerStyle>{children}</InnerStyle>
    </ButtonStyle>
  );
};

export const PrimaryButton = applyComponentTheme<ButtonProps>(
  (props) => <Button variant="primary" {...props} />,
  'PrimaryButton'
);

export const SecondaryButton = applyComponentTheme<ButtonProps>(
  (props) => <Button variant="secondary" {...props} />,
  'SecondaryButton'
);

export const TertiaryButton = applyComponentTheme<ButtonProps>(
  (props) => <Button variant="tertiary" {...props} />,
  'TertiaryButton'
);

export default applyComponentTheme(Button, 'Button');
